// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Deling — sharing a case with groups and by read-only links (ruxd-wmc.3/.4),
 * and the public `/del/<token>` landing page. Pure logic only: who may do
 * what, which groups can be offered, expiry presets, link edits, state labels
 * and the Danish copy for every refusal. The panels and the page render it.
 */

import type { ShareLinkCreate, ShareLinkPatch } from '../api/auth';
import { ApiRequestError } from '../api/client';
import type { AuthMe, CaseGroupShare, CaseRole, GlobalRole, Group, ShareLink, ShareLinkPublic } from '../api/types';
import { canManageMembers } from '../app/auth';
import { CASES_PATH, caseHref, shareUrl } from '../app/cases';
import type { Tone } from '../kortlaegning/vocab';

// --- permissions -------------------------------------------------------------

export interface Caller {
  /** The caller's role in the case (`GET /cases/{cid}`). */
  caseRole: CaseRole | null | undefined;
  /** The caller's global role (`GET /auth/me`). */
  globalRole: GlobalRole | undefined;
}

const isAdmin = (c: Caller) => c.globalRole === 'admin';

/** Project management rights: the case's owners, and an admin. */
export function canManageProject(c: Caller): boolean {
  return isAdmin(c) || canManageMembers(c.caseRole);
}

/** Sees which groups the case is shared with: any member of the case (and an admin). */
export function canSeeGroupShares(c: Caller): boolean {
  return isAdmin(c) || !!c.caseRole;
}

/** Shares the case with any group, raises or lowers a share's role, and stops a share: owners and admins. */
export function canManageGroupShares(c: Caller): boolean {
  return canManageProject(c);
}

/** Lists, creates, edits and revokes the case's share links: owners and admins (the server 403s the rest). */
export function canManageShareLinks(c: Caller): boolean {
  return canManageProject(c);
}

// --- group shares ------------------------------------------------------------

/** The groups the case can still be shared with: every group, minus those it is already shared with, by name. */
export function offerableGroups(groups: readonly Group[], shares: readonly CaseGroupShare[]): Group[] {
  const shared = new Set(shares.map((s) => s.group.id));
  return groups.filter((g) => !shared.has(g.id)).sort((a, b) => a.name.localeCompare(b.name, 'da'));
}

/** Group shares, editors first, then by group name. */
export function sortGroupShares(shares: readonly CaseGroupShare[]): CaseGroupShare[] {
  const rank = { editor: 0, viewer: 1 } as const;
  return [...shares].sort((a, b) => rank[a.role] - rank[b.role] || a.group.name.localeCompare(b.group.name, 'da'));
}

/** "1 medlem" / "4 medlemmer". */
export function memberCountText(n: number): string {
  return `${n} ${n === 1 ? 'medlem' : 'medlemmer'}`;
}

export type GroupShareAction = 'share' | 'role' | 'unshare';

/** The Danish line for a failed group-share change. */
export function groupShareErrorText(cause: unknown, action: GroupShareAction): string {
  if (cause instanceof ApiRequestError) {
    if (cause.status === 404 && action === 'share') return 'Gruppen findes ikke længere.';
    if (cause.status === 404) return 'Gruppen har ikke længere adgang til sagen.';
    if (cause.status === 403 && action === 'unshare') return 'Kun sagens ejere og administratorer kan stoppe en deling.';
    if (cause.status === 403) return 'Kun sagens ejere og administratorer kan dele sagen med grupper.';
    if (cause.status === 409) return 'Sagen er allerede delt med den gruppe. Skift rollen i stedet.';
    if (cause.status === 400) return 'En gruppe kan kun få rollen Redaktør eller Læser.';
  }
  return 'Ændringen kunne ikke gemmes.';
}

// --- share links -------------------------------------------------------------

export type ExpiryPreset = '1d' | '7d' | '30d' | 'never' | 'date';

export const EXPIRY_PRESETS: readonly ExpiryPreset[] = ['1d', '7d', '30d', 'never', 'date'];

export const EXPIRY_PRESET_LABELS: Record<ExpiryPreset, string> = {
  '1d': '1 dag',
  '7d': '7 dage',
  '30d': '30 dage',
  never: 'Aldrig',
  date: 'Vælg dato',
};

const DAY_MS = 24 * 60 * 60 * 1000;
const PRESET_DAYS: Partial<Record<ExpiryPreset, number>> = { '1d': 1, '7d': 7, '30d': 30 };

/** The server refuses an expiry more than ten years away. */
export const MAX_EXPIRY_YEARS = 10;

export const LABEL_MAX = 100;
export const PASSWORD_MIN = 4;
export const PASSWORD_MAX = 200;

/**
 * The `expires_at` a preset means, as ISO-8601; `undefined` = never.
 * `date` is a `YYYY-MM-DD` from a date input: the link lasts through that
 * day, local time. `null` when the input is missing, invalid, not in the
 * future, or more than ten years away.
 */
export function expiryIso(preset: ExpiryPreset, now: Date, date = ''): string | undefined | null {
  if (preset === 'never') return undefined;
  const days = PRESET_DAYS[preset];
  if (days !== undefined) return new Date(now.getTime() + days * DAY_MS).toISOString();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const end = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59);
  if (end.getMonth() !== Number(m[2]) - 1) return null;
  const limit = new Date(now);
  limit.setFullYear(limit.getFullYear() + MAX_EXPIRY_YEARS);
  if (end.getTime() <= now.getTime() || end.getTime() > limit.getTime()) return null;
  return end.toISOString();
}

/** A `YYYY-MM-DD` for a date input's `min` (tomorrow) and `max` (ten years). */
export function dateInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function dateInputBounds(now: Date): { min: string; max: string } {
  const min = new Date(now.getTime() + DAY_MS);
  const max = new Date(now);
  max.setFullYear(max.getFullYear() + MAX_EXPIRY_YEARS);
  return { min: dateInputValue(min), max: dateInputValue(max) };
}

export interface NewLinkDraft {
  label: string;
  /** Empty: no password. */
  password: string;
  preset: ExpiryPreset;
  /** `YYYY-MM-DD`, for `preset: 'date'`. */
  date: string;
}

/** The `POST /share-links` body (`ShareLinkCreate`). */
export type ShareLinkBody = ShareLinkCreate;

/** The `POST /share-links` body for a draft, or the Danish reason it cannot be sent. */
export function newLinkBody(draft: NewLinkDraft, now: Date): { body: ShareLinkBody } | { error: string } {
  const label = draft.label.trim();
  if (label.length > LABEL_MAX) return { error: `Navnet må højst være ${LABEL_MAX} tegn.` };
  if (draft.password !== '' && draft.password.length < PASSWORD_MIN)
    return { error: `Adgangskoden skal være mindst ${PASSWORD_MIN} tegn.` };
  if (draft.password.length > PASSWORD_MAX) return { error: `Adgangskoden må højst være ${PASSWORD_MAX} tegn.` };
  const expires = expiryIso(draft.preset, now, draft.date);
  if (expires === null) return { error: 'Vælg en dato fra i morgen og højst ti år frem.' };
  const body: ShareLinkBody = {};
  if (label) body.label = label;
  if (draft.password) body.password = draft.password;
  if (expires) body.expires_at = expires;
  return { body };
}

// --- editing a link ----------------------------------------------------------

/** What an edit does to the expiry: keep it, or any of the presets. */
export type EditExpiry = 'keep' | ExpiryPreset;

export const EDIT_EXPIRY_CHOICES: readonly EditExpiry[] = ['keep', ...EXPIRY_PRESETS];

/** What an edit does to the password. */
export type PasswordChange = 'keep' | 'set' | 'remove';

export interface EditLinkDraft {
  label: string;
  expiry: EditExpiry;
  /** `YYYY-MM-DD`, for `expiry: 'date'`. */
  date: string;
  passwordChange: PasswordChange;
  /** The new password, for `passwordChange: 'set'`. */
  password: string;
}

/** A fresh edit form for `link`: its name, everything else unchanged. */
export function editLinkDraft(link: ShareLink): EditLinkDraft {
  return { label: link.label, expiry: 'keep', date: '', passwordChange: 'keep', password: '' };
}

/** A revoked link is history: the server refuses to change it (409). Expired links may get a new expiry. */
export function canEditLink(link: ShareLink): boolean {
  return link.state !== 'revoked';
}

/** An expiry choice's label in the edit form; `keep` names the current expiry. */
export function editExpiryLabel(choice: EditExpiry, link: ShareLink, now: Date): string {
  if (choice === 'keep') return `Uændret (${expiryText(link.expires_at, now).toLowerCase()})`;
  if (choice === 'never') return 'Aldrig';
  if (choice === 'date') return EXPIRY_PRESET_LABELS.date;
  return `Om ${EXPIRY_PRESET_LABELS[choice]}`;
}

/**
 * The `PATCH /share-links/{id}` body for an edit — only what differs from
 * `link` — or the Danish reason it cannot be sent. An empty patch means
 * there is nothing to save.
 */
export function editLinkPatch(link: ShareLink, draft: EditLinkDraft, now: Date): { patch: ShareLinkPatch } | { error: string } {
  const patch: ShareLinkPatch = {};
  const label = draft.label.trim();
  if (label.length > LABEL_MAX) return { error: `Navnet må højst være ${LABEL_MAX} tegn.` };
  if (label !== link.label) patch.label = label;
  if (draft.expiry === 'never') {
    if (link.expires_at !== null) patch.expires_at = null;
  } else if (draft.expiry !== 'keep') {
    const expires = expiryIso(draft.expiry, now, draft.date);
    if (!expires) return { error: 'Vælg en dato fra i morgen og højst ti år frem.' };
    patch.expires_at = expires;
  }
  if (draft.passwordChange === 'set') {
    if (draft.password.length < PASSWORD_MIN) return { error: `Adgangskoden skal være mindst ${PASSWORD_MIN} tegn.` };
    if (draft.password.length > PASSWORD_MAX) return { error: `Adgangskoden må højst være ${PASSWORD_MAX} tegn.` };
    patch.password = draft.password;
  } else if (draft.passwordChange === 'remove' && link.has_password) {
    patch.password = null;
  }
  return { patch };
}

/** Whether a patch changes anything. */
export function hasChanges(patch: ShareLinkPatch): boolean {
  return Object.keys(patch).length > 0;
}

/** Whether a patch ends the sessions already opened through the link: setting or removing its password does. */
export function endsSessions(patch: ShareLinkPatch): boolean {
  return 'password' in patch;
}

/** Shown under the password choice in the edit form. */
export const PASSWORD_SESSIONS_NOTE =
  'Når du sætter eller fjerner adgangskoden, lukkes alle sessioner, der allerede er åbnet gennem linket. ' +
  'Modtagerne skal åbne det igen.';

/** The confirmation after a saved edit. */
export function savedLinkText(patch: ShareLinkPatch): string {
  if (patch.password === null)
    return 'Adgangskoden er fjernet, og alle åbne sessioner gennem linket er lukket. Linket åbner nu uden adgangskode.';
  if (typeof patch.password === 'string')
    return 'Adgangskoden er skiftet, og alle åbne sessioner gennem linket er lukket. Send den nye adgangskode ad en anden vej end linket.';
  return 'Ændringerne er gemt.';
}

export const LINK_STATE_LABELS: Record<ShareLink['state'], string> = {
  active: 'Aktiv',
  expired: 'Udløbet',
  revoked: 'Tilbagekaldt',
};

export const LINK_STATE_TONES: Record<ShareLink['state'], Tone> = {
  active: 'good',
  expired: 'wait',
  revoked: 'crit',
};

/** Active links first, newest first within each state. */
export function sortShareLinks(links: readonly ShareLink[]): ShareLink[] {
  const rank = { active: 0, expired: 1, revoked: 2 } as const;
  return [...links].sort((a, b) => rank[a.state] - rank[b.state] || b.created_at.localeCompare(a.created_at));
}

/** A date as "15.10.2026"; the input unchanged when it does not parse. */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** A moment as "15.10.2026 14.05". */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const time = d.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
  return `${formatDate(iso)} ${time}`;
}

/** "Udløber 15.10.2026", "Udløb 15.10.2026", "Udløber aldrig". */
export function expiryText(expiresAt: string | null, now: Date): string {
  if (!expiresAt) return 'Udløber aldrig';
  return new Date(expiresAt).getTime() <= now.getTime()
    ? `Udløb ${formatDate(expiresAt)}`
    : `Udløber ${formatDate(expiresAt)}`;
}

/** A listed link's validity: a revoked link's revocation date, else its expiry. */
export function linkValidityText(link: ShareLink, now: Date): string {
  if (link.state === 'revoked') return link.revoked_at ? `Tilbagekaldt ${formatDate(link.revoked_at)}` : 'Tilbagekaldt';
  return expiryText(link.expires_at, now);
}

/** "1 aktiv" / "2 aktive". */
export function activeCountText(links: readonly ShareLink[]): string {
  const n = links.filter((l) => l.state === 'active').length;
  return `${n} ${n === 1 ? 'aktiv' : 'aktive'}`;
}

/** "Brugt 15.10.2026 14.05" / "Aldrig brugt". */
export function lastUsedText(lastUsedAt: string | null): string {
  return lastUsedAt ? `Brugt ${formatDateTime(lastUsedAt)}` : 'Aldrig brugt';
}

/** Who made the link: their name or email; "Ukendt" for the superuser or a deleted account. */
export function creatorText(link: ShareLink): string {
  return link.created_by ? link.created_by.display_name || link.created_by.email : 'Ukendt';
}

/** A link's name, or a stand-in when it has none. */
export function linkLabel(label: string): string {
  return label.trim() || 'Link uden navn';
}

/** The full URL of a freshly created link, on this origin. */
export function createdLinkUrl(origin: string, token: string): string {
  return shareUrl(origin, token);
}

export type LinkAction = 'list' | 'create' | 'update' | 'revoke';

/** Why a caller without project management rights sees no links. */
export const SHARE_LINKS_DENIED_TEXT =
  'Delingslinks hører til sagen. Kun sagens ejere og administratorer kan se, oprette, ændre og tilbagekalde dem.';

/** The Danish line for a failed share-link request. */
export function shareLinkErrorText(cause: unknown, action: LinkAction): string {
  if (cause instanceof ApiRequestError) {
    if (cause.status === 403) return 'Kun sagens ejere og administratorer kan styre delingslinks.';
    if (cause.status === 404 && (action === 'revoke' || action === 'update')) return 'Linket findes ikke længere.';
    if (cause.status === 409 && action === 'update') return 'Linket er tilbagekaldt og kan ikke ændres.';
    if (cause.status === 400 && action === 'create')
      return 'Linket kunne ikke oprettes: tjek navn, adgangskode (4–200 tegn) og udløbsdato.';
    if (cause.status === 400 && action === 'update')
      return 'Ændringen blev afvist: tjek navn, adgangskode (4–200 tegn) og udløbsdato.';
  }
  if (action === 'list') return 'Kunne ikke hente sagens delingslinks.';
  if (action === 'revoke') return 'Linket kunne ikke tilbagekaldes.';
  if (action === 'update') return 'Ændringen kunne ikke gemmes.';
  return 'Linket kunne ikke oprettes.';
}

// --- the landing page (/del/<token>) ------------------------------------------

export type LandingFailure = { title: string; text: string; retry: boolean };

/** What the landing page says when `GET /share/{token}` fails. */
export function landingInfoFailure(cause: unknown, retryAfterSeconds?: number | null): LandingFailure {
  if (cause instanceof ApiRequestError) {
    if (cause.status === 404)
      return {
        title: 'Linket findes ikke',
        text: 'Tjek, at du har hele adressen med. Spørg den, der sendte linket, om et nyt.',
        retry: false,
      };
    if (cause.status === 410)
      return {
        title: 'Linket er udløbet eller tilbagekaldt',
        text: 'Det giver ikke længere adgang til sagen. Spørg den, der sendte linket, om et nyt.',
        retry: false,
      };
    if (cause.status === 429) return { title: 'For mange forsøg', text: waitText(retryAfterSeconds), retry: true };
    if (cause.status === 409)
      return {
        title: 'Delte links findes ikke her',
        text: 'Serveren kører i lokal tilstand, uden brugere og uden delte links.',
        retry: false,
      };
  }
  return {
    title: 'Linket kunne ikke åbnes',
    text: 'Serveren svarer ikke lige nu. Prøv igen om lidt.',
    retry: true,
  };
}

function waitText(seconds?: number | null): string {
  if (!seconds || seconds <= 0) return 'Vent lidt, og prøv så igen.';
  if (seconds < 60) return `Vent ${seconds} sekunder, og prøv så igen.`;
  const minutes = Math.ceil(seconds / 60);
  return `Vent ${minutes} ${minutes === 1 ? 'minut' : 'minutter'}, og prøv så igen.`;
}

/** The Danish line for a failed `POST /share/{token}/session`. */
export function landingOpenErrorText(cause: unknown, requiresPassword: boolean, retryAfterSeconds?: number | null): string {
  if (cause instanceof ApiRequestError) {
    if (cause.status === 401) return requiresPassword ? 'Forkert adgangskode.' : 'Linket kræver en adgangskode.';
    if (cause.status === 404) return 'Linket findes ikke.';
    if (cause.status === 410) return 'Linket er udløbet eller tilbagekaldt.';
    if (cause.status === 429) return `For mange forsøg. ${waitText(retryAfterSeconds)}`;
  }
  return 'Linket kunne ikke åbnes. Prøv igen.';
}

/** "Gælder til 15.10.2026" / "Gælder uden udløb". */
export function landingExpiryText(info: ShareLinkPublic): string {
  return info.expires_at ? `Gælder til ${formatDate(info.expires_at)}` : 'Gælder uden udløb';
}

/** Where an opened link goes: the case it opens, or the case list as a last resort. */
export function landingTarget(me: AuthMe): string {
  const cid = me.share_link?.case ?? me.case_scope;
  return cid ? caseHref(cid) : CASES_PATH;
}
